import type { GroupLifeMember } from '@/features/quotes/context/group-life-quote.context';
import { groupLifeService } from '@/services';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

/**
 * Key parcial de um bucket (target) — invalidar com isto refetcha TODAS as
 * páginas/buscas daquele target. Usado pelos handlers de CRUD/upload.
 */
export function membersPageBucketKey(
  policyId: string | null,
  targetId: string,
) {
  return ['group-life', 'members-page', policyId, targetId] as const;
}

/**
 * Paginação SERVER-SIDE das vidas de um target (estipulante master ou um
 * subestipulante). Escala para 5000+ vidas — não carrega o conjunto inteiro.
 * Contagem (`total`) e paginação vêm do InsureMO; busca por nome é
 * server-side; qualquer página é alcançável, mesmo além de 1000 vidas.
 *
 * `search` deve chegar JÁ debounced pelo caller (evita refetch por tecla).
 */
export function useMembersPage(params: {
  policyId: string | null;
  targetId: string;
  pageNo: number;
  pageSize: number;
  search: string;
  enabled?: boolean;
}) {
  const query = useQuery({
    queryKey: [
      'group-life',
      'members-page',
      params.policyId,
      params.targetId,
      params.pageNo,
      params.pageSize,
      params.search,
    ],
    queryFn: async () => {
      if (!params.policyId) {
        return {
          total: 0,
          pageNo: params.pageNo,
          pageSize: params.pageSize,
          members: [],
        };
      }
      const res = await groupLifeService.listMembersPage({
        quoteId: params.policyId,
        targetId: params.targetId,
        pageNo: params.pageNo,
        pageSize: params.pageSize,
        search: params.search || undefined,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Falha ao carregar as vidas');
      }
      return res.data;
    },
    enabled: !!params.policyId && (params.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const members: GroupLifeMember[] = (query.data?.members ?? []).map(m => ({
    id: m.id ?? '',
    cpf: m.cpf,
    name: m.name,
    dateOfBirth: m.dateOfBirth,
    gender: (m.gender || 'M') as 'M' | 'F',
    insurableGroup: m.insurableGroup,
    capital: m.capital,
    awayFromWork: m.awayFromWork,
    cids: m.cids,
  }));

  return {
    members,
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
  };
}
